"use client";

import React, { createContext, useContext, useState, useMemo } from "react";

export type AggregationType = "daily" | "weekly" | "monthly";

interface AggregationContextType {
  aggregation: AggregationType;
  setAggregation: (aggregation: AggregationType) => void;
  showAggregationSelector: boolean;
  setShowAggregationSelector: (show: boolean) => void;
}

const AggregationContext = createContext<AggregationContextType | undefined>(
  undefined
);

const STORAGE_KEY = "frms-aggregation";

export function AggregationProvider({
  children
}: Readonly<{
  children: React.ReactNode;
}>) {
  const [aggregation, setAggregationState] = useState<AggregationType>(() => {
    // Try to load from localStorage
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (
        stored &&
        (stored === "daily" || stored === "weekly" || stored === "monthly")
      ) {
        return stored as AggregationType;
      }
    }
    // Default to daily
    return "daily";
  });

  const [showAggregationSelector, setShowAggregationSelector] =
    useState<boolean>(false);

  const setAggregation = (newAggregation: AggregationType) => {
    setAggregationState(newAggregation);
    // Persist to localStorage
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, newAggregation);
    }
  };

  const contextValue = useMemo(
    () => ({
      aggregation,
      setAggregation,
      showAggregationSelector,
      setShowAggregationSelector
    }),
    [aggregation, showAggregationSelector]
  );

  return (
    <AggregationContext.Provider value={contextValue}>
      {children}
    </AggregationContext.Provider>
  );
}

export function useAggregation() {
  const context = useContext(AggregationContext);
  if (!context) {
    throw new Error(
      "useAggregation must be used within an AggregationProvider"
    );
  }
  return context;
}
